// Wayland-inspired desktop environment prototype for OchrePoint admin
// Conceptual UI — simulates a tiling WM / compositor desktop inside a browser tab
import { useState, useCallback, useRef, useEffect } from 'react';

// ── Types ──────────────────────────────────────────────────────────────────────

interface AppDef {
  id: string;
  label: string;
  icon: string;
  color: string;
  route?: string;
  component?: 'filemanager' | 'settings' | 'terminal';
}

interface WinState {
  id: string;
  appId: string;
  title: string;
  icon: string;
  x: number;
  y: number;
  w: number;
  h: number;
  minimized: boolean;
  maximized: boolean;
  workspace: number;
  zIndex: number;
  route?: string;
  component?: AppDef['component'];
}

interface DragState {
  type: 'move' | 'resize-se' | 'resize-e' | 'resize-s';
  winId: string;
  startX: number;
  startY: number;
  origX: number;
  origY: number;
  origW: number;
  origH: number;
}

// ── Application registry ───────────────────────────────────────────────────────

const APPS: AppDef[] = [
  { id: 'dashboard',   label: 'Dashboard',      icon: 'dashboard',       color: '#6366f1', route: '/dashboard' },
  { id: 'jobs',        label: 'Jobs',            icon: 'construction',    color: '#f59e0b', route: '/jobs' },
  { id: 'calendar',    label: 'Calendar',        icon: 'calendar_month',  color: '#10b981', route: '/calendar' },
  { id: 'quotes',      label: 'Quotes',          icon: 'request_quote',   color: '#C77D3F', route: '/quotes' },
  { id: 'employees',   label: 'Employees',       icon: 'badge',           color: '#8b5cf6', route: '/employees' },
  { id: 'clients',     label: 'Clients',         icon: 'groups',          color: '#0ea5e9', route: '/clients' },
  { id: 'invoices',    label: 'Invoices',        icon: 'receipt_long',    color: '#ec4899', route: '/invoices' },
  { id: 'reports',     label: 'Reports',         icon: 'bar_chart',       color: '#14b8a6', route: '/reports' },
  { id: 'market-rates',label: 'Market Rates',    icon: 'price_check',     color: '#84cc16', route: '/market-rates' },
  { id: 'files',       label: 'File Manager',    icon: 'folder_open',     color: '#f97316', component: 'filemanager' },
  { id: 'settings',    label: 'Settings',        icon: 'settings',        color: '#64748b', component: 'settings' },
  { id: 'terminal',    label: 'Terminal',        icon: 'terminal',        color: '#1e293b', component: 'terminal' },
];

const WALLPAPERS = [
  'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
  'linear-gradient(135deg, #0d0d0d 0%, #1a0a00 50%, #2d1400 100%)',
  'linear-gradient(135deg, #0f2027 0%, #203a43 50%, #2c5364 100%)',
  'linear-gradient(135deg, #200122 0%, #6f0000 100%)',
  'linear-gradient(135deg, #000000 0%, #130f40 100%)',
];

const BASE_Z = 100;
let zCounter = BASE_Z;

// ── File Manager ──────────────────────────────────────────────────────────────

const FILE_TREE = [
  { name: 'Work',     icon: 'work',          children: [
    { name: 'Jobs',          icon: 'construction', route: '/jobs' },
    { name: 'Quotes',        icon: 'request_quote', route: '/quotes' },
    { name: 'Agreements',    icon: 'handshake', route: '/agreements' },
    { name: 'Change Orders', icon: 'change_circle', route: '/change-orders' },
  ]},
  { name: 'People',   icon: 'groups',        children: [
    { name: 'Clients',    icon: 'groups',  route: '/clients' },
    { name: 'Employees',  icon: 'badge',   route: '/employees' },
    { name: 'Crews',      icon: 'diversity_3', route: '/crews' },
  ]},
  { name: 'Finance',  icon: 'account_balance', children: [
    { name: 'Invoices',   icon: 'receipt_long', route: '/invoices' },
    { name: 'Payroll',    icon: 'payments', route: '/payroll' },
    { name: 'Reports',    icon: 'bar_chart', route: '/reports' },
  ]},
  { name: 'Resources', icon: 'inventory_2', children: [
    { name: 'Catalog',      icon: 'inventory_2', route: '/catalog' },
    { name: 'Vendors',      icon: 'storefront', route: '/vendors' },
    { name: 'Documents',    icon: 'description', route: '/documents' },
    { name: 'Market Rates', icon: 'price_check', route: '/market-rates' },
  ]},
  { name: 'System',   icon: 'settings',      children: [
    { name: 'Settings',       icon: 'settings', route: '/settings' },
    { name: 'Dev Dashboard',  icon: 'code', route: '/dev' },
    { name: 'Architecture',   icon: 'account_tree', route: '/architecture' },
  ]},
];

function FileManager({ onNavigate }: { onNavigate: (route: string) => void }) {
  const [expanded, setExpanded] = useState<string[]>(['Work']);
  const [hovered, setHovered] = useState<string | null>(null);

  return (
    <div className="h-full flex bg-[#1e1e2e] text-gray-200 overflow-hidden">
      {/* Sidebar */}
      <div className="w-44 bg-[#181825] border-r border-white/10 overflow-y-auto flex-shrink-0">
        <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">Places</div>
        {FILE_TREE.map(folder => (
          <div key={folder.name}>
            <button
              onClick={() => setExpanded(e => e.includes(folder.name) ? e.filter(x => x !== folder.name) : [...e, folder.name])}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-gray-300 hover:bg-white/5 rounded-md mx-1"
            >
              <span className="material-symbols-outlined text-sm text-gray-400">{folder.icon}</span>
              <span>{folder.name}</span>
              <span className={`material-symbols-outlined text-xs ml-auto transition-transform ${expanded.includes(folder.name) ? 'rotate-90' : ''}`}>chevron_right</span>
            </button>
            {expanded.includes(folder.name) && folder.children.map(f => (
              <button
                key={f.name}
                onMouseEnter={() => setHovered(f.name)}
                onMouseLeave={() => setHovered(null)}
                onClick={() => f.route && onNavigate(f.route)}
                className={`w-full flex items-center gap-2 pl-8 pr-3 py-1.5 text-xs mx-1 rounded-md transition-colors ${hovered === f.name ? 'bg-[#C77D3F]/20 text-[#C77D3F]' : 'text-gray-400 hover:bg-white/5'}`}
              >
                <span className="material-symbols-outlined text-xs">{f.icon}</span>
                {f.name}
              </button>
            ))}
          </div>
        ))}
      </div>

      {/* Main area */}
      <div className="flex-1 p-4 overflow-y-auto">
        <div className="grid grid-cols-4 gap-3">
          {FILE_TREE.flatMap(f => f.children).map(item => (
            <button
              key={item.name}
              onDoubleClick={() => item.route && onNavigate(item.route)}
              className="flex flex-col items-center gap-1.5 p-3 rounded-xl hover:bg-white/10 group transition-colors"
            >
              <div className="w-12 h-12 bg-white/5 rounded-xl flex items-center justify-center group-hover:bg-[#C77D3F]/20 transition-colors">
                <span className="material-symbols-outlined text-2xl text-gray-300 group-hover:text-[#C77D3F]">{item.icon}</span>
              </div>
              <span className="text-xs text-gray-400 group-hover:text-gray-200 text-center leading-tight">{item.name}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Settings App ──────────────────────────────────────────────────────────────

const SETTINGS_SECTIONS = [
  { id: 'appearance', label: 'Appearance', icon: 'palette', desc: 'Wallpaper, theme, fonts, accent color' },
  { id: 'display', label: 'Display', icon: 'monitor', desc: 'Resolution, scaling, refresh rate' },
  { id: 'workspaces', label: 'Workspaces', icon: 'view_quilt', desc: 'Virtual desktops, workspace behavior' },
  { id: 'network', label: 'Network', icon: 'wifi', desc: 'Wi-Fi, VPN, proxy settings' },
  { id: 'accounts', label: 'Accounts', icon: 'manage_accounts', desc: 'User accounts, permissions, portal access' },
  { id: 'notifications', label: 'Notifications', icon: 'notifications', desc: 'App notifications, do-not-disturb' },
  { id: 'keyboard', label: 'Keyboard & Mouse', icon: 'keyboard', desc: 'Input device configuration, shortcuts' },
  { id: 'app-admin', label: 'App Administration', icon: 'admin_panel_settings', desc: 'OchrePoint settings, integrations, API keys' },
  { id: 'server', label: 'Server', icon: 'dns', desc: 'PM2 process status, Apache config, SSL, cron jobs' },
  { id: 'database', label: 'Database', icon: 'storage', desc: 'MySQL status, backups, migrations' },
  { id: 'security', label: 'Security', icon: 'security', desc: 'JWT secrets, CORS, rate limits, audit log' },
  { id: 'about', label: 'About', icon: 'info', desc: 'OchrePoint version, system info, license' },
];

function SettingsApp({ wallpaper, onWallpaperChange, wallpaperOptions }: {
  wallpaper: string;
  onWallpaperChange: (w: string) => void;
  wallpaperOptions: string[];
}) {
  const [active, setActive] = useState('appearance');
  const section = SETTINGS_SECTIONS.find(s => s.id === active);

  return (
    <div className="h-full flex bg-[#1e1e2e] text-gray-200 overflow-hidden">
      {/* Sidebar */}
      <div className="w-52 bg-[#181825] border-r border-white/10 overflow-y-auto flex-shrink-0 py-2">
        {SETTINGS_SECTIONS.map(s => (
          <button
            key={s.id}
            onClick={() => setActive(s.id)}
            className={`w-full flex items-center gap-2.5 px-4 py-2 text-sm mx-1 rounded-lg transition-colors ${active === s.id ? 'bg-[#C77D3F]/30 text-[#C77D3F]' : 'text-gray-400 hover:bg-white/5 hover:text-gray-200'}`}
          >
            <span className="material-symbols-outlined text-base">{s.icon}</span>
            {s.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <h2 className="text-lg font-semibold mb-1">{section?.label}</h2>
        <p className="text-sm text-gray-500 mb-6">{section?.desc}</p>

        {active === 'appearance' && (
          <div className="space-y-6">
            <div>
              <p className="text-sm font-medium text-gray-300 mb-3">Wallpaper</p>
              <div className="flex gap-3">
                {wallpaperOptions.map((w, i) => (
                  <button
                    key={i}
                    onClick={() => onWallpaperChange(w)}
                    className={`w-16 h-10 rounded-lg border-2 transition-all ${wallpaper === w ? 'border-[#C77D3F] scale-110' : 'border-transparent opacity-70 hover:opacity-100'}`}
                    style={{ background: w }}
                    title={`Wallpaper ${i + 1}`}
                  />
                ))}
              </div>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-300 mb-2">Accent Color</p>
              <div className="flex gap-2">
                {['#C77D3F','#6366f1','#10b981','#ec4899','#f59e0b','#0ea5e9'].map(c => (
                  <button key={c} className="w-7 h-7 rounded-full border-2 border-white/20 hover:scale-110 transition-transform" style={{ background: c }} />
                ))}
              </div>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-300 mb-2">Theme</p>
              <div className="flex gap-3">
                {['Wayland Dark','Catppuccin Mocha','Gruvbox Dark','Nord'].map(t => (
                  <button key={t} className="px-3 py-1.5 text-xs bg-white/5 hover:bg-white/10 rounded-lg border border-white/10 transition-colors">{t}</button>
                ))}
              </div>
            </div>
          </div>
        )}

        {active === 'server' && (
          <div className="space-y-4">
            <div className="bg-white/5 rounded-xl p-4">
              <p className="text-sm font-medium text-gray-300 mb-2">PM2 Processes</p>
              {[
                { name: 'onyxops', status: 'online', uptime: '2d 4h 12m', cpu: '1.2%', mem: '87MB' },
              ].map(p => (
                <div key={p.name} className="flex items-center gap-3 text-xs">
                  <span className="w-2 h-2 rounded-full bg-green-400" />
                  <span className="font-mono text-gray-300 w-20">{p.name}</span>
                  <span className="text-green-400 w-14">{p.status}</span>
                  <span className="text-gray-500">{p.uptime}</span>
                  <span className="text-gray-500 ml-4">CPU {p.cpu}</span>
                  <span className="text-gray-500">MEM {p.mem}</span>
                </div>
              ))}
            </div>
            <div className="bg-white/5 rounded-xl p-4">
              <p className="text-sm font-medium text-gray-300 mb-2">Cron Jobs</p>
              {[
                { schedule: '5:00 AM', task: 'Invoice overdue check + WCB expiry alerts' },
                { schedule: '5:00 PM', task: 'Timesheet SMS to scheduled employees' },
                { schedule: '16:30 + 18:00', task: 'Schedule reminders' },
              ].map(c => (
                <div key={c.schedule} className="flex items-start gap-3 text-xs py-1">
                  <span className="font-mono text-[#C77D3F] w-28 flex-shrink-0">{c.schedule}</span>
                  <span className="text-gray-400">{c.task}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {active === 'database' && (
          <div className="space-y-4">
            <div className="bg-white/5 rounded-xl p-4 space-y-2">
              <p className="text-sm font-medium text-gray-300">MySQL Status</p>
              <div className="flex items-center gap-2 text-xs">
                <span className="w-2 h-2 rounded-full bg-green-400" />
                <span className="text-green-400">Connected</span>
                <span className="text-gray-500 ml-2">construction_db @ localhost:3306</span>
              </div>
            </div>
          </div>
        )}

        {(active !== 'appearance' && active !== 'server' && active !== 'database') && (
          <div className="bg-white/5 rounded-xl p-6 text-center">
            <span className="material-symbols-outlined text-4xl text-gray-500 mb-3 block">{section?.icon}</span>
            <p className="text-sm text-gray-400">{section?.label} settings — prototype placeholder</p>
            <p className="text-xs text-gray-600 mt-1">Full implementation available in the main OchrePoint settings</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Terminal ──────────────────────────────────────────────────────────────────

function Terminal() {
  const [lines, setLines] = useState<{ type: 'cmd' | 'out'; text: string }[]>([
    { type: 'out', text: 'OchrePoint Shell v1.0 — Wayland Desktop Environment' },
    { type: 'out', text: 'Type "help" for available commands.' },
    { type: 'out', text: '' },
  ]);
  const [input, setInput] = useState('');
  const bottomRef = useRef<HTMLDivElement>(null);

  const handleCommand = (cmd: string) => {
    const parts = cmd.trim().split(' ');
    const c = parts[0].toLowerCase();
    const newLines: typeof lines = [{ type: 'cmd', text: `[emartin@ochrepoint ~]$ ${cmd}` }];
    if (c === 'help') newLines.push({ type: 'out', text: 'Commands: help, clear, version, status, whoami, ls, uptime, neofetch' });
    else if (c === 'clear') { setLines([]); return; }
    else if (c === 'version') newLines.push({ type: 'out', text: 'OchrePoint v0.0.0 — Node.js/Express + React 18 + MySQL 8 + PM2' });
    else if (c === 'whoami') newLines.push({ type: 'out', text: 'emartin (owner) @ OchrePoint Contracting LTD' });
    else if (c === 'uptime') newLines.push({ type: 'out', text: `up 2 days, 4:12 — load avg: 0.08 0.05 0.04` });
    else if (c === 'ls') newLines.push({ type: 'out', text: 'dashboard/  jobs/  quotes/  invoices/  employees/  clients/  reports/  catalog/  documents/' });
    else if (c === 'status') newLines.push(
      { type: 'out', text: '✓ PM2 onyxops: online (pid 195134)' },
      { type: 'out', text: '✓ MySQL 8.0: connected — construction_db' },
      { type: 'out', text: '✓ Apache 2.4: running — ochrepoint.ca' },
    );
    else if (c === 'neofetch') newLines.push(
      { type: 'out', text: '        ███████████' },
      { type: 'out', text: '       ░░░░░░░░░░░░  OchrePoint OS' },
      { type: 'out', text: '       ░░░░░░░░░░░░  ─────────────' },
      { type: 'out', text: '       ░░░░C77D3F░░  OS: Wayland Desktop 1.0' },
      { type: 'out', text: '       ░░░░░░░░░░░░  Shell: Express.js TSX' },
      { type: 'out', text: '        ███████████  DB: MySQL 8.0.36' },
      { type: 'out', text: '                    Stack: React 18 + Vite 6' },
    );
    else newLines.push({ type: 'out', text: `bash: ${c}: command not found` });
    setLines(prev => [...prev, ...newLines]);
  };

  useEffect(() => { bottomRef.current?.scrollIntoView(); }, [lines]);

  return (
    <div className="h-full bg-[#0d0d0d] font-mono text-sm text-green-400 p-3 overflow-y-auto flex flex-col">
      <div className="flex-1">
        {lines.map((l, i) => (
          <div key={i} className={l.type === 'cmd' ? 'text-gray-300' : 'text-green-400'}>{l.text || ' '}</div>
        ))}
        <div ref={bottomRef} />
      </div>
      <div className="flex items-center gap-1 mt-1">
        <span className="text-[#C77D3F]">[emartin@ochrepoint ~]$</span>
        <input
          autoFocus
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { handleCommand(input); setInput(''); } }}
          className="flex-1 bg-transparent outline-none text-green-400 caret-green-400"
        />
      </div>
    </div>
  );
}

// ── Start Menu ────────────────────────────────────────────────────────────────

function StartMenu({ onLaunch, onClose }: { onLaunch: (app: AppDef) => void; onClose: () => void }) {
  const [search, setSearch] = useState('');
  const filtered = APPS.filter(a => a.label.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="absolute bottom-12 left-2 w-80 bg-[#1e1e2e]/95 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl overflow-hidden z-[9999]" onClick={e => e.stopPropagation()}>
      {/* Search */}
      <div className="p-3 border-b border-white/10">
        <div className="flex items-center gap-2 bg-white/5 rounded-xl px-3 py-2">
          <span className="material-symbols-outlined text-gray-400 text-sm">search</span>
          <input
            autoFocus
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search apps…"
            className="flex-1 bg-transparent text-sm text-gray-200 outline-none placeholder-gray-500"
          />
        </div>
      </div>

      {/* Apps grid */}
      <div className="p-3">
        <p className="text-xs text-gray-500 uppercase tracking-wider mb-2 px-1">All Applications</p>
        <div className="grid grid-cols-4 gap-1">
          {filtered.map(app => (
            <button
              key={app.id}
              onClick={() => { onLaunch(app); onClose(); }}
              className="flex flex-col items-center gap-1 p-2 rounded-xl hover:bg-white/10 transition-colors"
            >
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: app.color + '30' }}>
                <span className="material-symbols-outlined text-xl" style={{ color: app.color }}>{app.icon}</span>
              </div>
              <span className="text-xs text-gray-300 text-center leading-tight">{app.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Power row */}
      <div className="border-t border-white/10 px-3 py-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-[#C77D3F]/30 flex items-center justify-center">
            <span className="material-symbols-outlined text-sm text-[#C77D3F]">person</span>
          </div>
          <span className="text-xs text-gray-300">emartin</span>
        </div>
        <div className="flex gap-2">
          {['settings','restart_alt','power_settings_new'].map(ic => (
            <button key={ic} className="w-7 h-7 rounded-lg hover:bg-white/10 flex items-center justify-center">
              <span className="material-symbols-outlined text-sm text-gray-400">{ic}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Main Desktop Component ─────────────────────────────────────────────────────

export function DesktopEnvironmentTab() {
  const [windows, setWindows] = useState<WinState[]>([]);
  const [activeWin, setActiveWin] = useState<string | null>(null);
  const [workspace, setWorkspace] = useState(0);
  const [wallpaper, setWallpaper] = useState(WALLPAPERS[0]);
  const [showStart, setShowStart] = useState(false);
  const [showNotifs, setShowNotifs] = useState(false);
  const [drag, setDrag] = useState<DragState | null>(null);
  const desktopRef = useRef<HTMLDivElement>(null);

  // Window manipulation
  const launchApp = useCallback((app: AppDef) => {
    const id = `win-${Date.now()}`;
    const w = app.component === 'terminal' ? 680 : 880;
    const h = app.component === 'terminal' ? 400 : 560;
    const x = 80 + Math.random() * 80;
    const y = 40 + Math.random() * 40;
    zCounter++;
    setWindows(prev => [...prev, {
      id, appId: app.id, title: app.label, icon: app.icon,
      x, y, w, h, minimized: false, maximized: false,
      workspace, zIndex: zCounter, route: app.route, component: app.component,
    }]);
    setActiveWin(id);
  }, [workspace]);

  const focusWin = useCallback((id: string) => {
    zCounter++;
    setWindows(prev => prev.map(w => w.id === id ? { ...w, zIndex: zCounter } : w));
    setActiveWin(id);
  }, []);

  const closeWin = useCallback((id: string) => {
    setWindows(prev => prev.filter(w => w.id !== id));
    setActiveWin(null);
  }, []);

  const minimizeWin = useCallback((id: string) => {
    setWindows(prev => prev.map(w => w.id === id ? { ...w, minimized: true } : w));
    setActiveWin(null);
  }, []);

  const maximizeWin = useCallback((id: string) => {
    setWindows(prev => prev.map(w => w.id === id ? { ...w, maximized: !w.maximized } : w));
  }, []);

  const restoreWin = useCallback((id: string) => {
    zCounter++;
    setWindows(prev => prev.map(w => w.id === id ? { ...w, minimized: false, zIndex: zCounter } : w));
    setActiveWin(id);
  }, []);

  // Tiling: tile two windows side-by-side
  const tileWindows = useCallback(() => {
    const visible = windows.filter(w => !w.minimized && w.workspace === workspace);
    if (visible.length < 2) return;
    const desk = desktopRef.current;
    if (!desk) return;
    const { width: dw, height: dh } = desk.getBoundingClientRect();
    const half = Math.floor(dw / visible.length);
    setWindows(prev => prev.map(w => {
      const idx = visible.findIndex(v => v.id === w.id);
      if (idx === -1) return w;
      return { ...w, x: idx * half, y: 0, w: half, h: dh - 48, maximized: false };
    }));
  }, [windows, workspace]);

  // Drag handler
  const onMouseDown = useCallback((e: React.MouseEvent, winId: string, type: DragState['type']) => {
    e.preventDefault();
    const win = windows.find(w => w.id === winId);
    if (!win) return;
    focusWin(winId);
    setDrag({ type, winId, startX: e.clientX, startY: e.clientY, origX: win.x, origY: win.y, origW: win.w, origH: win.h });
  }, [windows, focusWin]);

  useEffect(() => {
    if (!drag) return;
    const onMove = (e: MouseEvent) => {
      const dx = e.clientX - drag.startX;
      const dy = e.clientY - drag.startY;
      setWindows(prev => prev.map(w => {
        if (w.id !== drag.winId) return w;
        if (drag.type === 'move') return { ...w, x: drag.origX + dx, y: Math.max(0, drag.origY + dy) };
        if (drag.type === 'resize-se') return { ...w, w: Math.max(320, drag.origW + dx), h: Math.max(200, drag.origH + dy) };
        if (drag.type === 'resize-e') return { ...w, w: Math.max(320, drag.origW + dx) };
        if (drag.type === 'resize-s') return { ...w, h: Math.max(200, drag.origH + dy) };
        return w;
      }));
    };
    const onUp = () => setDrag(null);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [drag]);

  // File manager navigation — opens target app
  const handleFMNavigate = useCallback((route: string) => {
    const app = APPS.find(a => a.route === route) ?? { id: route, label: route, icon: 'web', color: '#C77D3F', route };
    launchApp(app as AppDef);
  }, [launchApp]);

  const now = new Date();
  const timeStr = now.toLocaleTimeString('en-CA', { hour: '2-digit', minute: '2-digit', hour12: false });
  const dateStr = now.toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric' });

  const workspaceWins = windows.filter(w => !w.minimized && w.workspace === workspace);
  const minimizedWins = windows.filter(w => w.minimized);

  return (
    <div className="relative w-full h-full overflow-hidden select-none flex flex-col rounded-xl" style={{ background: wallpaper, minHeight: '600px' }}
      onClick={() => { setShowStart(false); setShowNotifs(false); }}
    >
      {/* Desktop area */}
      <div ref={desktopRef} className="flex-1 relative overflow-hidden"
        onDoubleClick={() => setShowStart(false)}
      >
        {/* Desktop icons */}
        <div className="absolute top-4 right-4 flex flex-col gap-3">
          {APPS.slice(0, 6).map(app => (
            <button
              key={app.id}
              onDoubleClick={() => launchApp(app)}
              className="flex flex-col items-center gap-1 group"
            >
              <div className="w-12 h-12 rounded-xl flex items-center justify-center shadow-lg backdrop-blur-sm border border-white/10 group-hover:scale-110 transition-transform"
                style={{ background: app.color + '40' }}>
                <span className="material-symbols-outlined text-2xl" style={{ color: app.color }}>{app.icon}</span>
              </div>
              <span className="text-xs text-white/80 font-medium drop-shadow">{app.label}</span>
            </button>
          ))}
        </div>

        {/* Windows */}
        {workspaceWins.map(win => (
          <div
            key={win.id}
            style={{
              position: 'absolute',
              left: win.maximized ? 0 : win.x,
              top: win.maximized ? 0 : win.y,
              width: win.maximized ? '100%' : win.w,
              height: win.maximized ? 'calc(100% - 0px)' : win.h,
              zIndex: win.zIndex,
            }}
            className="flex flex-col rounded-xl overflow-hidden border border-white/10 shadow-2xl"
            onClick={() => focusWin(win.id)}
          >
            {/* Title bar */}
            <div
              className={`flex items-center gap-2 px-3 py-2 flex-shrink-0 cursor-move select-none ${activeWin === win.id ? 'bg-[#181825]/90' : 'bg-[#1e1e2e]/70'} backdrop-blur-xl border-b border-white/10`}
              onMouseDown={e => onMouseDown(e, win.id, 'move')}
            >
              {/* Traffic lights */}
              <div className="flex gap-1.5 mr-1">
                <button onClick={e => { e.stopPropagation(); closeWin(win.id); }} className="w-3 h-3 rounded-full bg-red-500 hover:bg-red-400 transition-colors" />
                <button onClick={e => { e.stopPropagation(); minimizeWin(win.id); }} className="w-3 h-3 rounded-full bg-yellow-500 hover:bg-yellow-400 transition-colors" />
                <button onClick={e => { e.stopPropagation(); maximizeWin(win.id); }} className="w-3 h-3 rounded-full bg-green-500 hover:bg-green-400 transition-colors" />
              </div>
              <span className="material-symbols-outlined text-sm text-gray-400">{win.icon}</span>
              <span className="text-sm text-gray-200 font-medium flex-1 truncate">{win.title}</span>
              <span className="text-xs text-gray-500">{win.w}×{win.h}</span>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-hidden">
              {win.component === 'filemanager' && <FileManager onNavigate={route => { closeWin(win.id); handleFMNavigate(route); }} />}
              {win.component === 'settings' && <SettingsApp wallpaper={wallpaper} onWallpaperChange={setWallpaper} wallpaperOptions={WALLPAPERS} />}
              {win.component === 'terminal' && <Terminal />}
              {win.route && !win.component && (
                <iframe
                  src={win.route}
                  className="w-full h-full border-0 bg-[#FAF6F0]"
                  title={win.title}
                />
              )}
            </div>

            {/* Resize handle SE */}
            {!win.maximized && (
              <div
                className="absolute bottom-0 right-0 w-4 h-4 cursor-se-resize opacity-30 hover:opacity-80"
                onMouseDown={e => { e.stopPropagation(); onMouseDown(e, win.id, 'resize-se'); }}
              >
                <svg viewBox="0 0 16 16" className="text-white fill-current"><polygon points="16,0 16,16 0,16"/></svg>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* ── Taskbar ── */}
      <div className="h-11 flex-shrink-0 flex items-center gap-1 px-2 bg-[#1e1e2e]/85 backdrop-blur-2xl border-t border-white/10 relative z-[9000]">

        {/* Start button (Arch Linux logo) */}
        <button
          onClick={e => { e.stopPropagation(); setShowStart(v => !v); }}
          className={`w-9 h-9 rounded-lg flex items-center justify-center transition-colors ${showStart ? 'bg-[#C77D3F]/30' : 'hover:bg-white/10'}`}
        >
          <svg viewBox="0 0 24 24" className="w-5 h-5" fill="#C77D3F">
            <path d="M12 2L2 20h20L12 2zm0 3.5L18.5 18h-13L12 5.5z" />
          </svg>
        </button>

        {/* Divider */}
        <div className="w-px h-6 bg-white/10 mx-1" />

        {/* Running apps (taskbar buttons) */}
        <div className="flex items-center gap-0.5 flex-1">
          {windows.map(win => (
            <button
              key={win.id}
              onClick={() => win.minimized ? restoreWin(win.id) : (activeWin === win.id ? minimizeWin(win.id) : focusWin(win.id))}
              className={`flex items-center gap-1.5 px-2 h-8 rounded-lg text-xs transition-colors max-w-[140px] ${
                win.workspace !== workspace ? 'opacity-30' :
                activeWin === win.id && !win.minimized ? 'bg-[#C77D3F]/30 text-[#C77D3F] border border-[#C77D3F]/30' :
                win.minimized ? 'bg-white/5 text-gray-500' : 'bg-white/10 text-gray-300 hover:bg-white/15'
              }`}
            >
              <span className="material-symbols-outlined text-sm">{win.icon}</span>
              <span className="truncate">{win.title}</span>
            </button>
          ))}

          {/* Minimized list */}
          {minimizedWins.length > 0 && (
            <div className="flex items-center gap-0.5 ml-1 pl-1 border-l border-white/10">
              {minimizedWins.map(w => (
                <button key={w.id} onClick={() => restoreWin(w.id)} title={w.title}
                  className="w-7 h-7 rounded-lg bg-white/5 flex items-center justify-center hover:bg-white/10 opacity-50 hover:opacity-100">
                  <span className="material-symbols-outlined text-xs text-gray-400">{w.icon}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Workspace switcher */}
        <div className="flex items-center gap-0.5 mx-2">
          {[0, 1, 2, 3].map(i => (
            <button
              key={i}
              onClick={() => setWorkspace(i)}
              className={`w-7 h-6 rounded text-xs font-medium transition-colors ${workspace === i ? 'bg-[#C77D3F]/40 text-[#C77D3F]' : 'text-gray-500 hover:bg-white/10 hover:text-gray-300'}`}
            >
              {i + 1}
            </button>
          ))}
        </div>

        {/* Tiling quick-tile button */}
        <button onClick={tileWindows} title="Tile windows" className="w-8 h-8 rounded-lg hover:bg-white/10 flex items-center justify-center">
          <span className="material-symbols-outlined text-sm text-gray-400">view_column</span>
        </button>

        {/* System tray */}
        <div className="flex items-center gap-1 ml-1">
          {['wifi','volume_up'].map(ic => (
            <button key={ic} className="w-7 h-7 rounded flex items-center justify-center hover:bg-white/10">
              <span className="material-symbols-outlined text-sm text-gray-400">{ic}</span>
            </button>
          ))}

          {/* Notifications */}
          <button
            onClick={e => { e.stopPropagation(); setShowNotifs(v => !v); }}
            className="w-7 h-7 rounded flex items-center justify-center hover:bg-white/10"
          >
            <span className="material-symbols-outlined text-sm text-gray-400">notifications</span>
          </button>
        </div>

        {/* Clock */}
        <button className="ml-1 text-right hover:bg-white/10 rounded-lg px-2 h-9 flex flex-col items-end justify-center">
          <span className="text-xs font-semibold text-gray-200 font-mono leading-none">{timeStr}</span>
          <span className="text-xs text-gray-500 leading-none">{dateStr}</span>
        </button>
      </div>

      {/* Start Menu overlay */}
      {showStart && <StartMenu onLaunch={launchApp} onClose={() => setShowStart(false)} />}

      {/* Notification panel */}
      {showNotifs && (
        <div className="absolute bottom-12 right-2 w-72 bg-[#1e1e2e]/95 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl p-4 z-[9999]" onClick={e => e.stopPropagation()}>
          <p className="text-sm font-semibold text-gray-200 mb-3">Notifications</p>
          {[
            { icon: 'schedule', text: 'Timesheet SMS sent to 3 workers', time: '5:00 PM' },
            { icon: 'check_circle', text: 'Timesheet approved: Alex M.', time: '4:32 PM' },
            { icon: 'receipt_long', text: 'Invoice #INV-2026-042 viewed by client', time: '2:15 PM' },
          ].map((n, i) => (
            <div key={i} className="flex items-start gap-2.5 py-2 border-b border-white/5 last:border-0">
              <span className="material-symbols-outlined text-sm text-[#C77D3F] mt-0.5">{n.icon}</span>
              <div className="flex-1">
                <p className="text-xs text-gray-300">{n.text}</p>
                <p className="text-xs text-gray-600">{n.time}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Onboarding hint (first launch) */}
      {windows.length === 0 && (
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center pointer-events-none">
          <p className="text-white/30 text-sm">Double-click desktop icons or use the Start button to launch apps</p>
          <p className="text-white/20 text-xs mt-1">Drag title bars to move · Resize from corner · Workspaces 1-4</p>
        </div>
      )}
    </div>
  );
}
